type AwsJsonPrimitive = string | number | boolean | null;
export type AwsJsonValue = AwsJsonPrimitive | AwsJsonValue[] | { [key: string]: AwsJsonValue };

const normalizeAwsJsonValue = (value: unknown): AwsJsonValue | null => {
  const visited = new WeakSet<object>();
  const serialized = JSON.stringify(value, (_key, candidate) => {
    if (typeof candidate === 'bigint') {
      return candidate.toString();
    }
    if (typeof candidate === 'number' && !Number.isFinite(candidate)) {
      return null;
    }
    if (typeof candidate === 'function' || typeof candidate === 'symbol') {
      return undefined;
    }
    if (candidate && typeof candidate === 'object') {
      if (visited.has(candidate as object)) {
        return undefined;
      }
      visited.add(candidate as object);
    }
    return candidate;
  });

  if (typeof serialized !== 'string') {
    return null;
  }
  try {
    return JSON.parse(serialized) as AwsJsonValue;
  } catch {
    return null;
  }
};

export const sanitizeAwsJsonValue = (value: unknown): AwsJsonValue =>
  normalizeAwsJsonValue(value) ?? null;

export const sanitizeAwsJsonObject = (
  value: unknown,
): Record<string, AwsJsonValue> => {
  const normalized = sanitizeAwsJsonValue(value);
  if (!normalized || typeof normalized !== 'object' || Array.isArray(normalized)) {
    return {};
  }
  return normalized as Record<string, AwsJsonValue>;
};

export const sanitizeAwsJsonArray = (value: unknown): AwsJsonValue[] => {
  const normalized = sanitizeAwsJsonValue(value);
  return Array.isArray(normalized) ? normalized : [];
};
